import { Injectable } from '@angular/core';
import { BatchActionService } from './batch-action.service';
import { Subscription } from 'rxjs';
import { BroadcastService } from './broadcast.service';
import { LoggerService } from 'src/app/core/logger.service';
import { SyncCryptService } from 'src/app/core/crypt/sync-crypt.service';
import { NotificationsService } from 'src/app/core/notifications.service';
import { ApiService } from 'src/app/core/api.service';
import { JoblockService } from 'src/app/core/joblock.service';
import { WebSocketService } from 'src/app/core/websocket.service';
import { BackgroundMoveApiInput, BackgroundMoveApiOutput } from '../models/api/backgroundmove-api.model';
import { LinkApiOutput } from '../models';
import { ProgressPayload } from '../models/websocket/action-payload';
import { MessageType } from '../models/websocket/websocket-message';
import { ProcessIndicatorService } from './process-indicator.service';


@Injectable({
    providedIn: 'root'
})
export class AtomicRenameService extends BatchActionService {

    private progressSubscription: Subscription;
    
    constructor(
        public broadcastService: BroadcastService,
        public loggerService: LoggerService,
        public syncCryptService: SyncCryptService,
        public notificationsService: NotificationsService,
        public apiService: ApiService,
        private jobLockService: JoblockService,
        private webSocketService: WebSocketService,
        private log: LoggerService,
        private processIndicatorService: ProcessIndicatorService
    ) {
        super(
            broadcastService,
            loggerService,
            syncCryptService,
            notificationsService,
            apiService
        );
    }

    public async atomicRename(syncId: number, newName: string, is_publink: number): Promise<BackgroundMoveApiOutput | undefined> {
        this.loggerService.info(`atomicRename(${syncId}, ${newName})`);
        this.view.globalCancel = false;
        this.ACTION = this.ACT_RENAME_SINGLE;
        this.view.percent = 0;

        try {
            const encName = await this.syncCryptService.filenameEncrypt(newName);
            const backgroundMovePayload: BackgroundMoveApiInput = { sync_id: syncId, name: encName };

            if (is_publink) {
                try {
                    const linkDetails = await this.apiService.execute<LinkApiOutput>('linkget', { sync_id: syncId });
                    if (linkDetails) {
                        const shareKeyId = `${linkDetails.share_id}-${linkDetails.share_sequence}`;
                        const sharekey = await this.syncCryptService.sharekeyDecrypt(linkDetails.enc_share_key, shareKeyId);
                        const encShareName = await this.syncCryptService.filenameEncrypt(newName, sharekey);
                        if (encShareName) {
                            backgroundMovePayload.publink = {
                                share_id: linkDetails.share_id,
                                enc_share_name: encShareName,
                            };
                        }
                    }
                } catch (err) {
                    this.loggerService.error(
                        `Failed to encrypt share_name for publink during rename operation. Sync ID: ${syncId}, New Name: ${newName}`
                    );

                }
            }

            this.progressSubscription = this.webSocketService
                .getMessageHandler<ProgressPayload>(MessageType.MOVE_PROGRESS)
                .subscribe((progress) => {
                    this.log.info('atomicRename Received MOVE_PROGRESS update:');
                    this.log.info(progress);
                    if (progress.sync_id === syncId) {
                        this.view.percent = progress.progressPercentage;
                        this.percentSubject.next(this.view.percent);
                    }
                });

            const resp = await this.apiService.execute<BackgroundMoveApiOutput>('backgroundmove', backgroundMovePayload);
            const bg = resp['bg'];
            if (bg) {
                // If background processing is required, poll the job status
                await this.jobLockService.pollJobStatus();
                this.processIndicatorService.hideProcessIndicator();
                this.loggerService.info('AtomicRename_JobComplete');
            }
            return resp; 
        } catch (err) {
            this.loggerService.handleError('atomicRename', err);
            return undefined; 
        } finally {
            if (this.progressSubscription) {
                this.progressSubscription.unsubscribe();
            }
        }
    }
}
